import { queryOptions } from "@tanstack/react-query";
import {
  DEFAULT_PUBLIC_SETTINGS,
  getIntegrationStatus,
  getPublicSettings,
  getSettings,
  type IntegrationStatus,
  type PublicSettings,
  type SettingsResponse,
} from "./settings.functions";

export type { IntegrationStatus, PublicSettings, SettingsResponse };

export const SETTINGS_QUERY_KEY = ["admin_settings"] as const;
export const PUBLIC_SETTINGS_QUERY_KEY = ["public_settings"] as const;
export const INTEGRATION_STATUS_QUERY_KEY = ["admin_integration_status"] as const;

/** Admin: the full editable settings document. */
export const settingsQueryOptions = () =>
  queryOptions({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: () => getSettings(),
    staleTime: 60_000,
  });

/**
 * Admin: live Shopify / Turnstile / analytics status for Settings > Integrations.
 * Each call probes the Storefront API, so it is not refetched on focus.
 */
export const integrationStatusQueryOptions = () =>
  queryOptions({
    queryKey: INTEGRATION_STATUS_QUERY_KEY,
    queryFn: () => getIntegrationStatus(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

/**
 * Public: rendered by the footer, contact links and the head meta. Placeholder
 * data is the shipped defaults, so nothing flashes or shifts before the call
 * returns, and a failed call leaves the defaults in place.
 */
export const publicSettingsQueryOptions = () =>
  queryOptions({
    queryKey: PUBLIC_SETTINGS_QUERY_KEY,
    queryFn: async (): Promise<PublicSettings> => {
      try {
        return await getPublicSettings();
      } catch {
        return DEFAULT_PUBLIC_SETTINGS;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
    placeholderData: DEFAULT_PUBLIC_SETTINGS,
  });
